import DragAndDropLoadImage, {
  ACCEPTED_IMAGE_TYPES,
} from "@/components/form-fields/DragAndDropLoadImage";
import { MultilingualField } from "@/components/form-items/MultilingualField";
import { Form } from "@/components/ui/form";
import { constructLocaleDict, getMainLanguage } from "@/i18n";
import { sectionImageCaptionSchema, Selection } from "@/lib/cms/section-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ImportImageForm } from "./SectionForm";
import { useTranslation } from "react-i18next";
import { useEditSelection, useImportImage } from "@/api/section";
import { useToast } from "@/hooks/use-toast";
import { uploadImage } from "@/api/image";
import { Attachment } from "@/api/attachment";
import { useAuth } from "@/context/auth/AuthContext";
import { Trash2 } from "lucide-react";
import { LoaderButton } from "@/components/ui/custom-buttons";
import { generateImageName } from "@/lib/utils";

const formSchema = z.object({
  image: z
    .instanceof(File, { message: "form.error.required" })
    .refine(
      (file) => {
        return ACCEPTED_IMAGE_TYPES.includes(file.type);
      },
      {
        message: "form.error.fileNotSupported",
      },
    )
    .optional(),
  caption: sectionImageCaptionSchema,
});

type FormData = z.infer<typeof formSchema>;

type ImportImageFormProps = {
  sectionId: string | undefined;
  sectionName: string;
  selection?: Selection;
  onDelete?: () => void;
  onSuccess?: () => void;
};

export const SectionImportImageForm = ({
  sectionId,
  sectionName,
  selection,
  onDelete,
  onSuccess,
}: ImportImageFormProps) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();
  const { toast } = useToast();
  const { mutateAsync: importImage } = useImportImage(sectionId);
  const { mutateAsync: editSelection } = useEditSelection(sectionId);

  const defaultValues: FormData = {
    image: undefined,
    caption: constructLocaleDict((lang) => {
      return {
        content: selection?.caption?.[lang]?.content ?? "",
      };
    }),
  };

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const onSubmit = async (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    // Url already defined so this is edit mode
    if (selection?.item.url) {
      await editSelection(
        {
          importDto: {
            imageId: selection.item.id,
            caption: submittedData.caption,
          },
        },
        {
          onError: (error) => {
            console.error("Error", error);
            toast({
              title: t("error"),
              description: t(
                "cms.selection.section.importImageForm.error.importFailed",
              ),
              variant: "error",
              duration: 1000,
            });
          },
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t(
                "cms.selection.section.importImageForm.error.importSuccess",
              ),
              variant: "success",
              duration: 1000,
            });
            onSuccess?.();
          },
        },
      );
      return;
    }

    if (!submittedData.image) {
      toast({
        title: t("error"),
        description: t(
          "cms.selection.section.importImageForm.error.imageRequired",
        ),
        variant: "error",
        duration: 1000,
      });
      return;
    }

    const response = submittedData.image
      ? await uploadImage(
          submittedData.image,
          {
            name: generateImageName({
              primaryType: "SectionImportedImage",
              sectionTitle: sectionName,
            }),
            position: "sectionImportedImage",
            docId: "",
            published: false,
          } as Attachment,
          instanceData.s3Bucket,
          instanceData.id,
        )
      : null;

    await importImage(
      {
        imageId: response?.id ?? "",
        caption: submittedData.caption,
      },
      {
        onError: (error) => {
          console.error("Error", error);
          toast({
            title: t("error"),
            description: t(
              "cms.selection.section.importImageForm.error.importFailed",
            ),
            variant: "error",
            duration: 1000,
          });
        },
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t(
              "cms.selection.section.importImageForm.error.importSuccess",
            ),
            variant: "success",
            duration: 1000,
          });
          onSuccess?.();
          form.reset();
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        id="importImage"
        onSubmit={(e) => {
          e.stopPropagation();
          form.handleSubmit(onSubmit)(e);
        }}
        className="flex flex-col justify-between gap-5"
      >
        <div className="flex flex-col gap-5">
          {selection?.item.url ? (
            <div className="relative w-fit min-w-[200px] hover:cursor-pointer">
              <img src={selection.item.url} className="max-h-[350px]" />
              <div
                className="absolute inset-0 bottom-0 left-0 gap-1.5 bg-black bg-opacity-70 opacity-0 hover:opacity-100 duration-300"
                onClick={onDelete}
              >
                <div className="flex flex-row justify-center items-center w-full h-full">
                  <Trash2 color="#dc2626" size={20} />
                  <span className="font-semibold text-base text-red-600">
                    {t("cms.selection.section.artworks.editImageForm.delete")}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <DragAndDropLoadImage
              control={form.control}
              getValues={form.getValues}
              config={{ name: "image", type: "inputFile" }}
              className="bg-white border border-solid"
            />
          )}

          <MultilingualField
            render={(lang) => <ImportImageForm lang={lang} />}
            fixed={getMainLanguage()}
          />
          <MultilingualField
            render={(lang) => <ImportImageForm lang={lang} />}
            excludeLang={getMainLanguage()}
          />
        </div>
        <div className="flex flex-col">
          <LoaderButton
            type="submit"
            form="importImage"
            disabled={form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
          >
            {selection?.item.url
              ? t("cms.selection.section.artworks.editImageForm.button")
              : t("cms.selection.section.artworks.importNewImageForm.button")}
          </LoaderButton>
        </div>
      </form>
    </Form>
  );
};
